import type { CollisionDescriptor, CollisionDetection } from '@dnd-kit/core'

/**
 * Sort collisions from smallest to greatest value
 */
function sortCollisionsAsc(
  { data: { value: a } }: CollisionDescriptor,
  { data: { value: b } }: CollisionDescriptor,
) {
  return a - b
}

/**
 * Finds the best fit for a draggable element in a vertical layout
 * where the droppables expand to match the dragged element's height
 */
export const itineraryCollisionDetection: CollisionDetection = (args) => {
  const { collisionRect, droppableRects, droppableContainers, active } = args

  const lockedToDate = active?.data?.current?.lockedToDate

  const draggedElementCentre = collisionRect.top + collisionRect.height * 0.5

  // If the active element is locked to a date, only allow collision with droppables for that date
  const dateFilter: (
    droppableContainer: (typeof droppableContainers)[0],
  ) => boolean = lockedToDate ?
      (droppableContainer) => {
        const [containerDate] = (droppableContainer.id as string).split(':')
        return containerDate === lockedToDate
      } :
      () => true

  const verticallySortedDroppables = droppableContainers
    .filter((droppableContainer) => droppableRects.has(droppableContainer.id))
    .filter(dateFilter)
    .sort(
      (a, b) => droppableRects.get(a.id)!.top - droppableRects.get(b.id)!.top,
    )

  let topOffset = 0

  const collisions = verticallySortedDroppables.map<CollisionDescriptor>(
    (droppableContainer) => {
      const rect = droppableRects.get(droppableContainer.id)!

      // Where the top would be if all the ones above were collapsed
      const offsetTop = rect.top - topOffset

      // Where the centre would be if only this droppable was expanded
      let droppableExpandedCentre = offsetTop + collisionRect.height * 0.5

      // Fudge the value a bit towards the dragged element, to make the selection more stable
      const distance = Math.abs(draggedElementCentre - droppableExpandedCentre)
      const fudgeFactor = Math.min(distance, rect.height * 0.2)
      if (draggedElementCentre < droppableExpandedCentre) {
        droppableExpandedCentre -= fudgeFactor
      } else {
        droppableExpandedCentre += fudgeFactor
      }

      topOffset += rect.height

      return {
        id: droppableContainer.id,
        data: {
          droppableContainer,
          value: Math.abs(droppableExpandedCentre - draggedElementCentre),
        },
      }
    },
  )

  return collisions.sort(sortCollisionsAsc)
}
